/**
 * Admin Layout - Persistent Layout Component
 *
 * This is the recommended Inertia.js pattern for handling flash messages.
 * The layout persists across page navigations and has access to usePage().
 */

import { useEffect } from 'react'
import { usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import { Toaster } from '@/components/ui/sonner'
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar'
import { AdminSidebar } from '@/components/admin-sidebar'
import { MobileSidebarTrigger } from '@/components/mobile-sidebar-trigger'

interface FlashMessages {
  success?: string
  error?: string
  notice?: string
  alert?: string
}

interface PageProps {
  flash?: FlashMessages
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  [key: string]: any
}

interface AdminLayoutProps {
  children: React.ReactNode
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { props } = usePage<PageProps>()
  const flash = props.flash
  const auth = props.auth

  // Show toast notifications when flash messages change
  useEffect(() => {
    if (!flash) return

    if (flash.success) {
      toast.success(flash.success)
    }

    if (flash.notice) {
      toast.success(flash.notice)
    }

    if (flash.error || flash.alert) {
      const message = flash.error || flash.alert
      toast.error(message)
    }
  }, [flash])

  return (
    <>
      <Toaster position="top-right" />
      <SidebarProvider defaultOpen={true}>
        <AdminSidebar user={auth.user} />
        <SidebarInset>
          <MobileSidebarTrigger />
          {children}
        </SidebarInset>
      </SidebarProvider>
    </>
  )
}
